/**
 * Dates. Stored and sent as ISO 8601 (`YYYY-MM-DD`, timestamps in UTC with an offset); shown to
 * and typed by people as `MM-DD-YYYY`. The two functions below are the only boundary, the same
 * rule the desk keeps, so a date of birth typed on the phone and a date of birth typed in the CRM
 * are the same value.
 */

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** True for a real calendar date in ISO form: "2026-02-30" is not one. */
export function isIsoDate(value: string): boolean {
  const m = ISO_DATE.exec(value);
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const date = new Date(Date.UTC(y, mo - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === mo - 1 && date.getUTCDate() === d;
}

/** "2026-09-15" → "09-15-2026". Anything that is not an ISO date is returned unchanged. */
export function toUsDate(iso: string | null | undefined): string {
  const s = String(iso ?? "").trim();
  if (!isIsoDate(s)) return s;
  return `${s.slice(5, 7)}-${s.slice(8, 10)}-${s.slice(0, 4)}`;
}

/**
 * "09-15-2026" → "2026-09-15", or null when it is not a real date. Slashes, dots and single-digit
 * months or days are accepted on the way in, as is an ISO date pasted from somewhere else.
 */
export function toIsoDate(us: string | null | undefined): string | null {
  const s = String(us ?? "").trim();
  if (!s) return null;
  if (isIsoDate(s)) return s;
  const m = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/.exec(s);
  if (!m) return null;
  const iso = `${m[3]}-${m[1]!.padStart(2, "0")}-${m[2]!.padStart(2, "0")}`;
  return isIsoDate(iso) ? iso : null;
}

/** Whole years between a date of birth and `on`. Insurance age (nearest birthday) is the carrier's. */
export function ageOn(dobIso: string, on: Date = new Date()): number | null {
  if (!isIsoDate(dobIso)) return null;
  const [y, m, d] = dobIso.split("-").map(Number) as [number, number, number];
  let age = on.getFullYear() - y;
  const beforeBirthday = on.getMonth() + 1 < m || (on.getMonth() + 1 === m && on.getDate() < d);
  if (beforeBirthday) age -= 1;
  return age >= 0 ? age : null;
}
